/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { getStrings } from "@microsoft/shifts-experiences-shared";
import { IShiftEntity } from "sh-models";
import { TeamStore } from "sh-stores";
import { TagStore } from "sh-stores/sh-tag-store";

import DateTimeFormatter, { DateTimeFormatType } from "../../../../utility/DateTimeFormatter";
import ShiftUtils from "../../../../utility/ShiftUtils";
import { BaseShiftCardProps } from "../BaseShiftCardProps";

import { CrossLocationShiftCardProps } from "./CrossLocationShiftCardProps";

/**
 * Gets the first line of text - the title and time range.
 * @example "Title, 12/31/2020, 12:00 PM - 1:00 PM"
 * @param shift The shift.
 * @returns The first line of text.
 */
const getTitle = (shift: IShiftEntity): string => {
    const { title, startTime, endTime } = shift;

    const date = DateTimeFormatter.getDateTimeRangeAsString(startTime, endTime, DateTimeFormatType.DateTime_DateShort_Time);

    if (title) {
        return getStrings("shifts", "crossLocationRequestTitle", [title, date]);
    } else {
        return date;
    }
};

/**
 * Gets the second line of text - either group/tag name, or notes.
 * @param shift The shift.
 * @returns The second line of text.
 */
const getSecondaryText = (shift: IShiftEntity): string => {
    const { notes } = shift;

    const tagId = ShiftUtils.getTagIdFromShift(shift);
    // TODO(CLM): Get the tag for external shifts.
    const tag = TagStore().tags.get(tagId);
    const tagName = tag && tag.name;

    return tagName ?? notes;
};

/**
 * Gets the third line of text - the team/store name.
 * @param teamName The team name.
 * @returns The third line of text.
 */
const getTertiaryText = (teamName?: string): string => {
    if (teamName?.length > 0) {
        return teamName;
    }

    return TeamStore().name;
};

/**
 * Gets the lines to be displayed in the shift card.
 * @param props The cross location shift props.
 * @param teamName The team name.
 * @returns The shift details.
 */
export const getBaseShiftCardProps = (props: CrossLocationShiftCardProps, teamName?: string): BaseShiftCardProps => {
    const { shift } = props;
    const { theme } = shift;

    const title = getTitle(shift);
    const secondaryText = getSecondaryText(shift);
    const tertiaryText = getTertiaryText(teamName);

    return {
        theme: theme,
        title: title, // If title is empty string, don't show it
        secondaryText: secondaryText,
        tertiaryText: tertiaryText,
        icon: "teams-location-add" // Cross Location icon
    };
};